// "Add Lead" ka form (PounceAddLead ke modal me): naam, phone, email, company,
// lead source, notes aur consent. Phone aur naam zaroori; baaki optional.
import React, { useRef, useState, useEffect } from "react";
import Loader from "../../Loader/Loader";
import styles from "./PounceLeadForm.module.css";

const icon = (paths) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {paths}
  </svg>
);

const ICONS = {
  name: icon(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>,
  ),
  phone: icon(
    <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />,
  ),
  email: icon(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </>,
  ),
  company: icon(
    <>
      <rect x="4" y="3" width="11" height="18" rx="1.5" />
      <path d="M15 9h5v12H4M8 7h3M8 11h3M8 15h3" />
    </>,
  ),
  source: icon(<path d="M5 20V10M12 20V4M19 20v-7" />),
  inquiry: icon(
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" />
      <path d="M14 3v6h6M8 13h8M8 17h5" />
    </>,
  ),
};

// `value` server pe lead ka `source` banta hai. "manual" purana default hai —
// leads / appointments screens ise "Manual Add" dikhati hain.
export const LEAD_SOURCES = [
  { value: "manual", label: "Manual Add" },
  { value: "Phone Call", label: "Phone Call" },
  { value: "Walk-in", label: "Walk-in" },
  { value: "Referral", label: "Referral" },
  { value: "Website Form", label: "Website Form" },
  { value: "Google Ads", label: "Google Ads" },
  { value: "Facebook / Meta Ads", label: "Facebook / Meta Ads" },
  { value: "Other", label: "Other" },
];

// Validation browser ki apni hai (pattern / maxLength / type): galat value pe
// form submit nahi hota aur `title` wala message field ke paas dikhta hai.
// `clean` likhte waqt hi galat characters hata deta hai.
const FIELDS = [
  {
    name: "name",
    label: "Full Name",
    required: true,
    placeholder: "Enter full name",
    maxLength: 70,
    // Sirf akshar (kisi bhi bhasha ke) aur space.
    pattern: "[\\p{L} ]*\\p{L}[\\p{L} ]*",
    title: "Letters and spaces only, up to 70 characters.",
    clean: (v) => v.replace(/[^\p{L} ]/gu, ""),
  },
  {
    name: "phone",
    label: "Phone Number",
    type: "tel",
    required: true,
    placeholder: "+15551234567",
    inputMode: "tel",
    // 8 se 13 ank, aage "+" ho sakta hai.
    maxLength: 14,
    pattern: "\\+?[0-9]{8,13}",
    title: "Enter 8 to 13 digits. You can start with + and the country code.",
    clean: (v) => v.replace(/(?!^\+)\D/g, ""),
  },
  {
    name: "email",
    label: "Email Address",
    type: "email",
    placeholder: "name@company.com",
    maxLength: 100,
    // type="email" akela "a@b" bhi maan leta hai — domain ke baad ".xx" zaroori.
    pattern: "[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}",
    title: "Enter a valid email address, like name@company.com.",
    clean: (v) => v.trim(),
  },
  // { name: "company", label: "Company", placeholder: "Enter company name", maxLength: 100 },
];

// `fieldErrors` = { [field ka naam]: message } — us field ke neeche dikhta hai.
const PounceLeadForm = ({
  form,
  onFieldChange,
  onSubmit,
  saving,
  fieldErrors = {},
  submitLabel = "Add Lead & Start Calling",
}) => {
  const submitRef = useRef(null);
  const requiredFilled = !!(form?.name?.trim() && form?.phone?.trim());
  // Checkbox ka state
  const [consentGiven, setConsentGiven] = useState(false);
  useEffect(() => {
    if (!requiredFilled) {
      setConsentGiven(false);
    }
  }, [requiredFilled]);
  return (
    <form className={styles.form} onSubmit={onSubmit}>
      {/* Intro line ab modal ke header me hai (PounceAddLead ka title). */}
      <div className={styles.card}>
        {FIELDS.map(({ name, label, required, clean, ...inputProps }) => (
          <label key={name} className={styles.field}>
            <span className={styles.label}>
              {label}
              {!required && (
                <span className={styles.optional}> (optional)</span>
              )}
            </span>
            <span
              className={`${styles.control} ${fieldErrors[name] ? styles.controlError : ""}`}
            >
              {ICONS[name]}
              <input
                className={styles.input}
                value={form[name] || ""}
                onChange={(e) =>
                  onFieldChange(
                    name,
                    clean ? clean(e.target.value) : e.target.value,
                  )
                }
                required={required}
                // Lead ka data hai, user ka apna nahi — browser apna naam/email na bhare.
                autoComplete="off"
                aria-invalid={fieldErrors[name] ? true : undefined}
                {...inputProps}
              />
            </span>
            {fieldErrors[name] && (
              <span className={styles.fieldError} role="alert">
                {fieldErrors[name]}
              </span>
            )}
          </label>
        ))}

        <label className={styles.field}>
          <span className={styles.label}>Lead Source</span>
          <span className={styles.control}>
            {ICONS.source}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.source || "manual"}
              onChange={(e) => onFieldChange("source", e.target.value)}
            >
              {LEAD_SOURCES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </span>
        </label>

        {/* <label className={styles.field}>
        <span className={styles.label}>
          Notes<span className={styles.optional}> (optional)</span>
        </span>
        <span className={`${styles.control} ${styles.controlTop}`}>
          {ICONS.inquiry}
          <textarea
            className={`${styles.input} ${styles.textarea}`}
            name="inquiry"
            value={form.inquiry || ""}
            onChange={(e) => onFieldChange("inquiry", e.target.value)}
            placeholder="Add any additional notes..."
            rows={3}
            maxLength={2000}
          />
        </span>
      </label> */}
      </div>

      {/* Required: the server blocks leads without consent when
        POUNCE_REQUIRE_CONSENT=true, and an AI call needs it anyway. */}
      <div className={styles.consent} style={{ cursor: "default" }}>
        <input
          type="checkbox"
          required
          disabled={!requiredFilled}
          checked={consentGiven}
          onChange={(e) => setConsentGiven(e.target.checked)}
          style={{
            cursor: requiredFilled ? "pointer" : "not-allowed",
          }}
        />
        <span style={{ cursor: "default", userSelect: "none" }}>
          I confirm this lead has given permission to be contacted.
        </span>
      </div>

      <p className={styles.note}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8h.01" />
        </svg>
        <span>
          Outbound Caller only contacts eligible leads based on your calling
          settings.
        </span>
      </p>

      {/* Hidden native submit button preserves browser validation on required
        fields and triggers onSubmit. The visible compact button clicks it. */}
      <button type="submit" ref={submitRef} disabled={saving} hidden />
      <button
        type="button"
        className={styles.submitBtn}
        onClick={() => submitRef.current?.click()}
        disabled={saving || !requiredFilled || !consentGiven}
        aria-busy={saving}
      >
        {saving ? (
          <Loader size={18} />
        ) : (
          <span className={styles.submitContent}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              className={styles.plusIcon}
            >
              <circle cx="12" cy="12" r="11" fill="#ffffff" />
              <path
                d="M12 7.5v9M7.5 12h9"
                stroke="#6524EB"
                strokeWidth="2.6"
                strokeLinecap="round"
              />
            </svg>
            <span className={styles.submitText}>{submitLabel}</span>
          </span>
        )}
      </button>
    </form>
  );
};

export default PounceLeadForm;
